import { Observable, of } from 'rxjs';
import { EntityKeyByReference } from '@card-software/library';
import { PurchaseOrderReceivedQuantity } from '../../models/inventory/purchase-order-received-quantity';
import { SupplyWarehouseDocumentType } from '../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { TableColumn } from '../../models/supply-warehouse/table-column.model';
import { TransactionAction } from '../../models/supply-warehouse/transaction-action.model';

/**
 * Estrategia base abstracta para manejar transacciones de inventario
 * Implementa el patrón Strategy para diferentes tipos de documentos
 */
export abstract class BaseTransactionStrategy<TDocument, TStatus> {
  /** Tipo de documento que maneja esta estrategia */
  abstract readonly documentType: SupplyWarehouseDocumentType;

  /** Nombre del documento iniciador (ej: 'Purchase Order', 'Supply Request') */
  abstract readonly initiatorName: string;

  /** Tipo de asignación de ubicaciones requerida */
  abstract readonly locationAssignmentType: 'storage' | 'source' | null;

  // ==================== Métodos de Carga ====================

  /**
   * Carga un documento por su ID
   */
  abstract loadDocument(id: string): Observable<TDocument | null>;

  /**
   * Carga el documento iniciador (PO, SupplyRequest, etc.)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  abstract loadInitiatorDocument(initiatorId: string): Observable<any>;

  /**
   * Busca documentos de transacción asociados al iniciador
   */
  abstract searchTransactionDocuments(initiatorId: string): Observable<TDocument[]>;

  /**
   * Crea un nuevo documento desde el iniciador
   */
  abstract createFromInitiator(initiatorId: string, locationCode?: string): Observable<TDocument>;

  /**
   * Cantidades netas ya procesadas por item del iniciador, calculadas por el API.
   * Devuelve null cuando la estrategia no las expone y el total debe sumarse en cliente
   * a partir de los documentos de transacción.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  loadNetProcessedQuantities(initiatorId: string): Observable<PurchaseOrderReceivedQuantity[] | null> {
    return of(null);
  }

  // ==================== Métodos de Actualización ====================

  /**
   * Actualiza el documento completo
   */
  abstract updateDocument(id: string, document: TDocument): Observable<TDocument>;

  /**
   * Actualiza solo el estado del documento
   */
  abstract updateStatus(id: string, newStatus: TStatus): Observable<TDocument>;

  // ==================== Configuración de UI ====================

  /**
   * Obtiene las columnas para la tabla de líneas del documento
   */
  abstract getTableColumns(): TableColumn[];

  /**
   * Obtiene las columnas para la tabla de items del iniciador
   */
  abstract getInitiatorItemsColumns(): TableColumn[];

  /**
   * Obtiene las acciones disponibles según el estado
   */
  abstract getAvailableActions(status: TStatus, document: TDocument): TransactionAction[];

  // ==================== Reglas de Negocio ====================

  /**
   * Determina si se pueden editar cantidades en el estado actual
   */
  abstract canEditQuantities(status: TStatus): boolean;

  /**
   * Determina si se pueden editar líneas (agregar/eliminar)
   */
  abstract canEditLines(status: TStatus): boolean;

  /**
   * Determina si se requiere asignación de ubicaciones
   */
  requiresLocationAssignment(): boolean {
    return this.locationAssignmentType !== null;
  }

  /**
   * Valida que el documento esté listo para cambiar de estado
   */
  abstract validateForStatusChange(document: TDocument, newStatus: TStatus): { valid: boolean; errors: string[] };

  // ==================== Helpers ====================

  /**
   * Obtiene el título del documento para mostrar en el header
   */
  abstract getDocumentTitle(document: TDocument | null): string;

  /**
   * Obtiene el EntityKeyByReference para el iniciador
   */
  abstract getInitiatorEntityKey(initiatorId: string): EntityKeyByReference;

  // ==================== Report Download ====================

  /**
   * Indica si esta estrategia soporta descarga de reportes
   */
  supportsReportDownload(): boolean {
    return false;
  }

  /**
   * Indica si el reporte puede ser descargado según el estado del documento
   * Por defecto, solo permite descarga en estados Processing o Completed
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canDownloadReport(status: TStatus): boolean {
    return false;
  }

  /**
   * Descarga el reporte PDF del documento
   * Debe ser sobrescrito por las estrategias que soporten reportes
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  downloadReport(documentId: string): Observable<Blob> {
    return of(new Blob());
  }
}
